import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import CreditAssessmentAction from '../../../redux/actions/CreditAssessmentAction';
import PlansAction from '../../../redux/actions/PlansAction';
import SignupPaymentAction from '../../../redux/actions/SignupPaymentAction';
import { CheckoutSelector } from '../../../redux/reducers/CheckoutReducer';
import PageHeader from '../../PageHeader/PageHeader';
import ProgressBar from '../ProgressBar/ProgressBar';
import CreditAssessmentStep1 from '../CreditAssessmentStep1/CreditAssessmentStep1';
import CreditAssessmentStep2 from '../CreditAssessmentStep2/CreditAssessmentStep2';
import CreditAssessmentStep3 from '../CreditAssessmentStep3/CreditAssessmentStep3';
import CreditAssessmentStep3Failure from '../CreditAssessmentStep3Failure/CreditAssessmentStep3Failure';
import CreditAssessmentStep4 from '../CreditAssessmentStep4/CreditAssessmentStep4';
import './ContractCheckout.scss';

const MAX_RETRY = 5;

class ContractCheckout extends React.Component {
  constructor(props) {
    super(props);
    this.goToNextStep = this.goToNextStep.bind(this);
    this.goToPreviousStep = this.goToPreviousStep.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handlePrevious = this.handlePrevious.bind(this);
    this.handleProgressBarClick = this.handleProgressBarClick.bind(this);
    this.handleSwitchToOpenTerm = this.handleSwitchToOpenTerm.bind(this);
    this.hasSubmittedTooManyTimes = this.hasSubmittedTooManyTimes.bind(this);
    this.handlePopState = this.handlePopState.bind(this);
    this.renderContents = this.renderContents.bind(this);
    this.state = { step: 1 };
  }

  componentWillMount() {
    window.addEventListener('popstate', this.handlePopState);
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.handlePopState);
  }

  handlePopState(event) {
    event.preventDefault();
    if (event.state) {
      this.props.clearCaError();
      if (this.state.step === 2) {
        this.setState({ step: 1 });
      } else if (this.state.step === 3) {
        this.setState({ step: 2 });
      } else if (this.state.step === 4) {
        this.setState({ step: 3 });
      }
    }
  }

  goToNextStep(step, scrollToTop = true) {
    // The following line does not appear to work as documented but we need to add
    // something to the history when moving between steps.
    history.pushState(this.state, 'caHistory', document.location);
    this.setState({ step });
    if (scrollToTop) {
      window.scrollTo(0, 0);
    }
  }

  goToPreviousStep(step) {
    this.props.clearCaError();
    this.setState({ step });
  }

  handleNext(values) {
    if (this.state.step === 1) {
      this.goToNextStep(2);
      this.props.clearCaError();
    } else if (this.state.step === 2) {
      this.props.submitCreditAssessment(values)
        .then(() => {
          this.goToNextStep(3);
          // eslint-disable-next-line no-console
        }).catch(error => console.error(error));
    } else if (this.state.step === 3) {
      this.props.queryCreditAssessmentResult(values)
        .then(() => {
          if (this.props.isCaApproved) {
            this.props.resetCaSubmitCount();
          }
          // If there is no Payment Email the customer has not yet visited the payment page. In which case
          // we linger on the Checkout page for 2.5 seconds so they can see the CA passed green tick animation play.
          if (this.props.isCaApproved && !this.props.isCaResultViewed) {
            setTimeout(() => {
              this.props.setCaResultViewed();
              this.props.storePaymentDetails(values);
              this.goToNextStep(4);
            }, 1800);
          } else if (this.props.hasInvalidInformation && !this.hasSubmittedTooManyTimes()) {
            this.goToNextStep(1, false);
          } else {
            this.props.storePaymentDetails(values);
            this.goToNextStep(4);
          }
          // eslint-disable-next-line no-console
        }).catch(error => console.error(error));
    } else if (this.state.step === 4 && !this.props.isCaApproved) {
      this.goToNextStep(1);
    }
  }

  handlePrevious() {
    this.goToPreviousStep(this.state.step - 1);
  }

  handleProgressBarClick(step) {
    if (step < this.state.step) {
      this.goToPreviousStep(step);
    }
  }

  handleSwitchToOpenTerm() {
    this.props.switchToOpenTerm(false);
    window.scrollTo(0, 0);
  }

  hasSubmittedTooManyTimes() {
    return this.props.caSubmitCount > MAX_RETRY;
  }

  renderContents() {
    const { isCaApproved, isByoModemSelected } = this.props;
    if (this.hasSubmittedTooManyTimes() && !isCaApproved) {
      // if the retry limit is already passed but the customer is on the checkout page for some reason
      // (by back button or whatever), they should be taken to the failure page
      return (
        <div>
          <CreditAssessmentStep3Failure
            handleNext={this.handleNext}
            handleSwitchToOpenTerm={this.handleSwitchToOpenTerm}
            disableRetry={() => true}
          />
        </div>
      );
    }
    return (
      <div className="contract-checkout-page">
        {this.state.step === 1 &&
        <CreditAssessmentStep1 handleNext={this.handleNext} handleSwitchToOpenTerm={this.handleSwitchToOpenTerm} />
        }
        {this.state.step === 2 &&
        <CreditAssessmentStep2
          handleNext={this.handleNext}
          handlePrevious={this.handlePrevious}
          handleSwitchToOpenTerm={this.handleSwitchToOpenTerm}
        />
        }
        {this.state.step === 3 &&
        <CreditAssessmentStep3 isByoModemSelected={isByoModemSelected} handleNext={this.handleNext} handlePrevious={this.handlePrevious} />
        }
        {this.state.step === 4 &&
        (
          isCaApproved
            ? <CreditAssessmentStep4 />
            : <CreditAssessmentStep3Failure
              handleNext={this.handleNext}
              handleSwitchToOpenTerm={this.handleSwitchToOpenTerm}
              disableRetry={this.hasSubmittedTooManyTimes}
            />
        )
        }
      </div>
    );
  }

  render() {
    const { isCaApproved } = this.props;
    return (
      <main>
        <PageHeader title="Checkout" />
        <ProgressBar
          currentStep={this.state.step === 4 && !isCaApproved ? 3 : this.state.step}
          handleClick={this.handleProgressBarClick}
          disableClick={this.state.step === 4 && !isCaApproved}
          hidden={(!isCaApproved || this.hasSubmittedTooManyTimes()) && this.state.step === 4}
        />
        {this.renderContents()}
      </main>
    );
  }
}

const mapDispatchToProps = dispatch =>
  bindActionCreators({
    submitCreditAssessment: CreditAssessmentAction.submit,
    queryCreditAssessmentResult: CreditAssessmentAction.queryResult,
    resetCaSubmitCount: CreditAssessmentAction.resetCaSubmitCount,
    setCaResultViewed: CreditAssessmentAction.setCaResultViewed,
    clearCaError: CreditAssessmentAction.clearCaError,
    storePaymentDetails: SignupPaymentAction.storePaymentDetails,
    switchToOpenTerm: PlansAction.switchToOpenTerm
  }, dispatch);

const mapStateToProps = state => ({
  caSubmitCount: CheckoutSelector.getCaSubmitCount(state),
  isCaApproved: CheckoutSelector.isCaApproved(state),
  isCaResultViewed: CheckoutSelector.isCaResultViewed(state),
  hasInvalidInformation: CheckoutSelector.hasInvalidInformation(state),
});

ContractCheckout.propTypes = {
  caSubmitCount: PropTypes.number,
  isCaApproved: PropTypes.bool,
  hasInvalidInformation: PropTypes.bool,
  isCaResultViewed: PropTypes.bool,
  submitCreditAssessment: PropTypes.func,
  queryCreditAssessmentResult: PropTypes.func,
  resetCaSubmitCount: PropTypes.func,
  setCaResultViewed: PropTypes.func,
  clearCaError: PropTypes.func,
  storePaymentDetails: PropTypes.func,
  switchToOpenTerm: PropTypes.func,
  isByoModemSelected: PropTypes.bool
};

export default connect(mapStateToProps, mapDispatchToProps)(ContractCheckout);



/** WEBPACK FOOTER **
 ** ./src/components/checkout/Checkout/ContractCheckout.js
 **/